import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ChoosePlanCardComponent } from '@app/+authenticated/+account/billing/choose-plan/choose-plan-card/choose-plan-card.component';
import { BillingDetailsFormComponent } from '@app/+authenticated/+account/billing/forms/billing-details/billing-details-form.component';
import { OrderSummaryComponent } from '@app/+authenticated/+account/billing/order-summary/order-summary.component';
import { OrderCompletionComponent } from '@app/+authenticated/+account/billing/plan-wizard/order-completion/order-completion.component';
import { SelectedSubscriptionModel } from '@app/+authenticated/+reports/shared/subscriptions/subscription.model';
import { SubscriptionService } from '@app/+authenticated/+reports/shared/subscriptions/subscription.service';
import { MailtoPipe } from '@app/pipes/mailto.pipe';
import { getAccount } from '@app/reducers/account/account-service.selectors';
import { AccountModel } from '@app/reducers/account/account.model';
import { getAuthenticatedStatus } from '@app/reducers/auth/auth-service.selectors';
import { TrackingService } from '@app/services/tracking.service';
import { ChargebeePortalSections } from '@app/shared/billing/billing.model';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { featureFlag, FeatureFlagPipe } from '@sb/shared/feature';
import { ButtonComponent } from '@sb/ui/button';
import { NoticeComponent } from '@sb/ui/notice';
import { StepperComponent, type Step } from '@sb/ui/stepper';
import { BillingService } from '@shared/billing/billing.service';
import { SharedLaddaModule } from '@shared/shared-ladda.module';
import { InvoicePeriodType, PlanType, SubscriptionModel, TrackingEvent } from '@shiftbase-com/ts-shared/models';
import { catchError, of, Subscription, switchMap, take } from 'rxjs';

import { BillingAndSummaryComponent } from './billing-and-summary/billing-and-summary.component';
import { ConfirmDowngradeComponent } from './confirm-downgrade/confirm-downgrade.component';
import { PlanWizardService } from './plan-wizard.service';

enum PlanWizard {
  CHOOSE_PLAN,
  BILLING_AND_SUMMARY,
  ORDER_COMPLETE,
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'plan-wizard',
  templateUrl: './plan-wizard.component.html',
  imports: [
    ChoosePlanCardComponent,
    CommonModule,
    TranslatePipe,
    BillingDetailsFormComponent,
    OrderSummaryComponent,
    SharedLaddaModule,
    OrderCompletionComponent,
    BillingAndSummaryComponent,
    ButtonComponent,
    ConfirmDowngradeComponent,
    StepperComponent,
    NoticeComponent,
    MailtoPipe,
    FeatureFlagPipe,
  ],
})
export class PlanWizardComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  subscriptionService = inject(SubscriptionService);
  private billingService = inject(BillingService);
  private cd = inject(ChangeDetectorRef);
  private planWizard = inject(PlanWizardService);
  private store = inject<Store<AppState>>(Store);
  private route = inject(ActivatedRoute);
  private trackingService = inject(TrackingService);

  planWizardStep: any;

  skipBilling: boolean;
  selectedSubscription: SelectedSubscriptionModel;
  account: AccountModel;
  showConfirmDowngrade = false;

  Features = featureFlag;
  estimatesDone: boolean;

  @Input()
  forceUpgrade = false;

  @Output()
  wizardCompleted = new EventEmitter();
  @Output()
  checkoutLoading = new EventEmitter();
  @Output()
  exitWizard = new EventEmitter();
  @Output()
  restartWizard = new EventEmitter();

  @ViewChild(BillingAndSummaryComponent)
  billingAndSummary: BillingAndSummaryComponent;

  trialExpired = false;

  isLoading = false;

  PlanType = PlanType;

  steps: Step[] = [
    { label: this.translate.instant('Choose plan') },
    { label: this.translate.instant('Billing and summary') },
  ];
  currentStepIndex = 0;

  private dataSubs = new Subscription();

  yearlyBillingToggle = new FormControl(false);

  isSaving: boolean;

  nextBtnLabel: string;

  supportEmail: string;

  buttonDisabled: boolean;

  ngOnInit() {
    this.trackEvent('Plan Wizard Opened');

    this.dataSubs.add(
      this.store.select(getAccount).subscribe((account) => {
        this.account = account;
        this.setSupportEmail();
      }),
    );

    this.dataSubs.add(
      this.store.select(getAuthenticatedStatus).subscribe((status) => {
        this.trialExpired = status === 'expired';
      }),
    );

    this.updatePlanWizardSteps();
  }

  // Sets steps for the wizard, skipBilling and nextButtonDisabled
  private updatePlanWizardSteps() {
    this.dataSubs.add(
      this.subscriptionService
        .getSelectedSubscription()
        .subscribe((selectedSubscription: SelectedSubscriptionModel) => {
          this.selectedSubscription = selectedSubscription;

          this.skipBilling =
            this.account.continue_subscription || selectedSubscription?.subscription?.plan?.type === PlanType.FREE;

          this.planWizardStep = PlanWizard;

          if (this.skipBilling) {
            enum SkipPlanWizard {
              CHOOSE_PLAN,
              ORDER_SUMMARY,
              ORDER_COMPLETE,
            }
            this.planWizardStep = SkipPlanWizard;
          }

          this.setNextButtonLabelAndVisibility();
        }),
    );
  }

  private setNextButtonLabelAndVisibility() {
    if (this.currentStepIndex === this.planWizardStep.CHOOSE_PLAN && !this.skipBilling) {
      this.nextBtnLabel = this.translate.instant('Continue to billing');
    } else if (this.currentStepIndex === this.planWizardStep.CHOOSE_PLAN && this.skipBilling) {
      this.nextBtnLabel = this.translate.instant('Continue to summary');
    } else {
      this.nextBtnLabel = this.translate.instant('Next');
    }
  }

  get hasSelectedSubscription(): boolean {
    return !!this.selectedSubscription.subscription;
  }

  private setSupportEmail(): void {
    const dachCountries = ['DE', 'AT', 'CH'];
    this.supportEmail = dachCountries.includes(this.account?.country)
      ? 'info.dach@shiftbase.com'
      : 'info@shiftbase.com';
  }

  nextStep() {
    this.stepChanged(this.currentStepIndex + 1);
  }

  previousStep() {
    this.stepChanged(this.currentStepIndex - 1);
  }

  stepChanged(event: number) {
    this.showConfirmDowngrade = false;

    if (this.currentStepIndex === PlanWizard.ORDER_COMPLETE) {
      this.restartWizard.emit();
    }
    if (event === this.steps.length) {
      this.openCheckout();
      return;
    }
    this.currentStepIndex = event;
    this.setNextButtonLabelAndVisibility();
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  exitWiz() {
    this.trackEvent('Plan Wizard Completed');
    this.exitWizard.emit();
  }

  private openCheckout() {
    this.checkoutLoading.emit();
    const cbInstance = this.billingService.getChargeBeeInstance();
    cbInstance.logout();
    const cbPortalInstance = this.billingService.initChargebeePortalSession();
    cbPortalInstance.open(
      {
        paymentSourceAdd: () => {
          cbInstance.closeAll();
          this.completeWidget();
        },
        paymentSourceUpdate: () => {
          cbInstance.closeAll();
          this.completeWidget();
        },
        close: () => {
          this.completeWidget();
        },
      },
      { sectionType: ChargebeePortalSections.PAYMENT_SOURCES },
    );
  }

  submitBillingForm(downgradeConfirmed = false) {
    this.billingAndSummary?.billingDetailsForm?.billingDetailsForm.markAllAsTouched();

    if (this.billingAndSummary?.billingDetailsForm?.billingDetailsForm.invalid) {
      return;
    }

    if (!downgradeConfirmed && this.isDowngrade) {
      this.showConfirmDowngrade = true;

      return;
    }

    this.isLoading = true;

    this.dataSubs.add(
      this.billingAndSummary?.billingDetailsForm?.submit().subscribe({
        complete: () => {
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;

          this.trackEvent('Plan Wizard Failed');
        },
      }),
    );
  }

  private completeWidget() {
    this.currentStepIndex = this.currentStepIndex + 1;
    this.cd.detectChanges();
    this.wizardCompleted.emit();

    if (!this.planWizard.paymentValid$.value) {
      this.trackEvent('Plan Wizard Failed');
    }
  }

  get isDowngrade(): boolean {
    if (this.account.hasTrial || this.trialExpired) {
      return false;
    }
    return this.isPlanDowngrade || this.isIntegrationPlusDowngrade || this.isChangingBillingCycleToMonthly;
  }

  private get isIntegrationPlusDowngrade(): boolean {
    if (!this.selectedSubscription) {
      return false;
    }
    return this.account.integration_plus && !this.selectedSubscription.integrationPlus;
  }

  private get isChangingBillingCycleToMonthly(): boolean {
    if (!this.selectedSubscription) {
      return false;
    }
    return (
      this.selectedSubscription?.invoicePeriod === InvoicePeriodType.MONTH &&
      this.currentSubscription.invoice_period !== InvoicePeriodType.MONTH
    );
  }

  private get isPlanDowngrade(): boolean {
    if (!this.selectedSubscription) {
      return false;
    }
    return this.currentSubscription.plan.weight > this.selectedSubscription.subscription.plan.weight;
  }

  get currentSubscription(): SubscriptionModel {
    return this.subscriptionService.getCurrentSubscription();
  }

  cancelDowngrade() {
    this.showConfirmDowngrade = false;
  }

  confirmDowngrade() {
    if (this.currentStepIndex === this.planWizardStep.ORDER_SUMMARY && this.skipBilling) {
      this.confirmPlanChange(true);
      return;
    }

    if (this.currentStepIndex === this.planWizardStep.BILLING_AND_SUMMARY) {
      this.submitBillingForm(true);
      return;
    }
  }

  confirmPlanChange(downgradeConfirmed = false) {
    if (!downgradeConfirmed && this.isDowngrade) {
      this.showConfirmDowngrade = true;

      return;
    }

    this.isSaving = true;

    this.dataSubs.add(
      this.subscriptionService
        .getSelectedSubscription()
        .pipe(
          take(1),
          switchMap((selectedSubscription) =>
            this.subscriptionService
              .saveSubscription({
                subscriptionId: selectedSubscription.subscription.id,
                integrationPlus: selectedSubscription.integrationPlus,
                invoicePeriod: selectedSubscription.invoicePeriod,
                compliance: selectedSubscription.compliance,
                hrPlus: selectedSubscription.hrPlus,
                chat: selectedSubscription.chat,
              })
              .pipe(
                catchError(() => {
                  this.trackEvent('Plan Wizard Failed');
                  this.isSaving = false;
                  return of(null);
                }),
              ),
          ),
        )
        .subscribe(() => {
          this.planWizard.paymentValid$.next(true);
          this.isSaving = false;
          this.exitWiz();
        }),
    );
  }

  private trackEvent(event: TrackingEvent) {
    this.trackingService.event(event, {
      originator: this.trackingOriginator,
    });
  }

  private get trackingOriginator(): string {
    return this.route.routeConfig.path === 'trial-expired'
      ? 'trial-basic-or-premium-ends'
      : this.route.snapshot.params['originator'];
  }
}
